import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, Chip, useTheme } from '@mui/material';
import type { Odpoved } from '../api/JsdhNotifikaceService/generated/models/Odpoved';
import type { PravidlaVyjezdu } from '../api/JsdhNotifikaceService/generated/models/DeviceToken';
import { VyjezdSemafor } from './VyjezdSemafor';
import DojezdOdpocet from './DojezdOdpocet';
import { useNestihaVyjezd } from '../hooks/useNestihaVyjezd';
import { tintNestiha } from '../theme/nestihaTint';
import type { ZalohaJinde } from '../api/JsdhNotifikaceService/generated/models/ZalohaJinde';
import { BEZ_ZALOHY, rozdelOdpovedi } from '../utils/rozdelOdpovedi';
import { maxNejedou, spocitejRozlozeni } from '../utils/vyjezd2026Rozlozeni';
import { ZalohaSkupina } from './ZalohaSkupina';
import { viditelneRadky } from '../utils/viditelneRadky';
import type { Orientace } from '../hooks/useOrientaceKontejneru';

interface Vyjezd2026OdpovediSectionProps {
    odpovedi: Odpoved[];
    /** Lide v zaloze u jineho aktivniho poplachu (GET .../zaloha-jinde). */
    zalohaJinde?: ZalohaJinde[];
    /** ISO cas vyhlaseni poplachu — bez nej se odpocet dojezdu nevykresli. */
    casVyhlaseni?: string;
    pravidla?: PravidlaVyjezdu;
    showSpecializace?: boolean;
    showFunkce?: boolean;
    /** Rozložení: 'sirka' (výchozí) nebo 'vyska' (semafor v řádku + jména s měřeným počtem řádků). */
    rozlozeni?: Orientace;
    /** Jen na výšku: výška řádku semaforu v px (z vyskySekciNaVysku). */
    vyskaSemaforuPx?: number;
}

const ADAPTIVE_THRESHOLD = 6;
const MAX_SPEC_VISIBLE = 2;

interface MereniSloupce { sloupec: number; radek: number; dalsi: number }
interface MereniSloupcu { jedu: MereniSloupce; nejedu: MereniSloupce }
const PRAZDNE_MERENI: MereniSloupcu = {
    jedu: { sloupec: 0, radek: 0, dalsi: 0 },
    nejedu: { sloupec: 0, radek: 0, dalsi: 0 },
};
/**
 * Záložní výška řádku „+ N dalších" (1,25rem × 1,5 = 30 px se zaokrouhlenou rezervou). Na výšku se
 * jinak MĚŘÍ (skrytý měřicí řádek ve sloupci); tahle hodnota platí jen, když měření vrátí 0
 * (první vykreslení, jsdom).
 */
const ZALOZNI_VYSKA_RADKU_DALSICH_PX = 36;

// Vnitřní výška sloupce, výška jednoho řádku vč. spodního okraje a výška řádku „+ N dalších"
// v nezmenšených CSS px (clientHeight/offsetHeight transform: scale náhledu neovlivní). Bez
// vykresleného řádku si nechá poslední známou výšku – jinak by se počet rozkmital
// (0 řádků → nic k měření → všechny).
const zmerSloupec = (el: HTMLElement | null, predchozi: MereniSloupce): MereniSloupce => {
    if (!el) return predchozi;
    const cs = getComputedStyle(el);
    // Skupina „V záloze" sedí pod Jedou a má přednost: místo pro jména je výška sloupce bez ní.
    const zalohaEl = el.querySelector<HTMLElement>('[data-testid="zaloha-skupina"]');
    const zaloha = zalohaEl ? zalohaEl.offsetHeight + (parseFloat(getComputedStyle(zalohaEl).marginTop) || 0) : 0;
    const zbyva = el.clientHeight - (parseFloat(cs.paddingTop) || 0) - (parseFloat(cs.paddingBottom) || 0) - zaloha;
    // Změřený sloupec, kterému záloha nenechala místo, nesmí vypadat jako nezměřený (0 = „ukaž všechny"):
    // jména by pak vytlačila skupinu „V záloze" pod ořez.
    const sloupec = el.clientHeight > 0 ? Math.max(1, zbyva) : 0;
    const radekEl = el.querySelector<HTMLElement>('[data-radek]');
    const radek = radekEl
        ? radekEl.offsetHeight + (parseFloat(getComputedStyle(radekEl).marginBottom) || 0)
        : predchozi.radek;
    const dalsiEl = el.querySelector<HTMLElement>('[data-mereni-dalsi]');
    const dalsi = dalsiEl ? dalsiEl.offsetHeight : predchozi.dalsi;
    return { sloupec, radek, dalsi };
};
const rezervaDalsich = (m: MereniSloupce): number => (m.dalsi > 0 ? m.dalsi : ZALOZNI_VYSKA_RADKU_DALSICH_PX);

export const Vyjezd2026OdpovediSection: React.FC<Vyjezd2026OdpovediSectionProps> = ({
    odpovedi, zalohaJinde = BEZ_ZALOHY, pravidla, showSpecializace = true, showFunkce = true, casVyhlaseni,
    rozlozeni = 'sirka', vyskaSemaforuPx = 64,
}) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const cardTintJedu = isDark ? 'rgba(76, 175, 80, 0.22)' : 'rgba(46, 125, 50, 0.18)';
    const cardTintNejedu = isDark ? 'rgba(244, 67, 54, 0.22)' : 'rgba(198, 40, 40, 0.18)';
    const rozdeleni = useMemo(() => rozdelOdpovedi(odpovedi, zalohaJinde), [odpovedi, zalohaJinde]);
    // Dojezd a „nestiha“ jen pro ty, kdo jedou s vozem — zaloha uz je ve zbrojnici (spec § 5.2).
    const jeNestihajici = useNestihaVyjezd(casVyhlaseni, rozdeleni.jedou);
    const podkladNestiha = tintNestiha(theme, 0.18);

    const { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH, assignedRoleById } = useMemo(() => {
        const jedouOdpovedi = rozdeleni.jedou;
        const nejedouOdpovedi = rozdeleni.nejedou;
        // Pravidlo: každá JEDU osoba se započítá právě do jedné kategorie (VD / S / H), shodně se sloupcem JEDU.
        // - Multi-role VD+S: greedy — první obsadí VD slot, další se počítají do S.
        // - VD-only / S-only: počítáme přímo do své role.
        // - H se počítá jen pokud nemá VD ani S (v JSDH má roli "Hasič" baseline většina členů,
        //   takže nezávislé počítání by zdvojovalo velitele/strojníky a součet by neseděl s JEDU).
        let pocetVD = 0, pocetS = 0, pocetH = 0;
        const assignedRoleById = new Map<number, 'VD' | 'S' | 'H'>();
        // Single-role osoby zpracujeme dřív — nemají flexibilitu, takže priorita slotu nehraje roli.
        const sorted = [...jedouOdpovedi].sort(
            (a, b) => (a.funkce?.length ?? 0) - (b.funkce?.length ?? 0),
        );
        for (const o of sorted) {
            if (!o.funkce || o.funkce.length === 0) continue;
            const kody = new Set(o.funkce.map(f => f.kod));
            const isVD = kody.has('VD');
            const isS = kody.has('S');
            const isH = kody.has('H');
            let primary: 'VD' | 'S' | 'H' | null = null;
            if (isVD && isS) {
                if (pocetVD === 0) { pocetVD++; primary = 'VD'; }
                else { pocetS++; primary = 'S'; }
            } else if (isVD) {
                pocetVD++; primary = 'VD';
            } else if (isS) {
                pocetS++; primary = 'S';
            } else if (isH) {
                pocetH++; primary = 'H';
            }
            if (primary) assignedRoleById.set(o.id, primary);
        }
        return { jedouOdpovedi, nejedouOdpovedi, pocetVD, pocetS, pocetH, assignedRoleById };
    }, [rozdeleni]);

    const pocetZaloha = rozdeleni.zaloha.length;
    // Velke radky Jedou jen kdyz Jedou + zaloha <= 6 (UX F1); radky zalohy jsou vzdy kompaktni.
    const useAdaptiveJedu = jedouOdpovedi.length + pocetZaloha <= ADAPTIVE_THRESHOLD;
    // NEJEDU vždy compact — uživatelům stačí menší font, nepotřebujeme "first 4 large".
    const useAdaptiveNejedu = false;

    const containerRef = useRef<HTMLDivElement>(null);
    // Vyska kontejneru (0 = jeste nezmereno). Pocet radku Jedou, zalohy a Nejedou se pocita z ni
    // spolecne (utils/vyjezd2026Rozlozeni): zaloha ma prednost a Jedou dostanou zbytek.
    const [vyska, setVyska] = useState(0);

    useEffect(() => {
        // Na výšku se maxItemsJedu/Nejedu nepoužívají (řádky se měří níže).
        if (rozlozeni === 'vyska') return;
        const el = containerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(() => setVyska(el.clientHeight));
        observer.observe(el);
        return () => observer.disconnect();
        // rozlozeni: při přepnutí z výšky na šířku bez remountu musí efekt kontejner na šířku znovu navázat.
    }, [rozlozeni]);

    const { maxJedou: maxItemsJedu, maxZaloha } = spocitejRozlozeni({
        vyska, pocetJedou: jedouOdpovedi.length, pocetZaloha, velkeJedou: useAdaptiveJedu,
    });
    const maxItemsNejedu = maxNejedou(vyska);

    // Na výšku: počet řádků z MĚŘENÉ výšky sloupce, řádku i řádku „+ N dalších"
    // (konstanty 56/36 a MIN_VISIBLE se nepoužijí).
    const sloupecJeduRef = useRef<HTMLDivElement>(null);
    const sloupecNejeduRef = useRef<HTMLDivElement>(null);
    const [mereniNaVysku, setMereniNaVysku] = useState<MereniSloupcu>(PRAZDNE_MERENI);
    useLayoutEffect(() => {
        if (rozlozeni !== 'vyska') return undefined;
        const zmer = () => setMereniNaVysku(prev => {
            const jedu = zmerSloupec(sloupecJeduRef.current, prev.jedu);
            const nejedu = zmerSloupec(sloupecNejeduRef.current, prev.nejedu);
            const stejne = jedu.sloupec === prev.jedu.sloupec && jedu.radek === prev.jedu.radek
                && jedu.dalsi === prev.jedu.dalsi
                && nejedu.sloupec === prev.nejedu.sloupec && nejedu.radek === prev.nejedu.radek
                && nejedu.dalsi === prev.nejedu.dalsi;
            return stejne ? prev : { jedu, nejedu };
        });
        zmer();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const ro = new ResizeObserver(() => zmer());
        if (sloupecJeduRef.current) ro.observe(sloupecJeduRef.current);
        if (sloupecNejeduRef.current) ro.observe(sloupecNejeduRef.current);
        return () => ro.disconnect();
    }, [rozlozeni, jedouOdpovedi.length, nejedouOdpovedi.length, pocetZaloha]);

    // Pro NEJEDU se nezobrazují funkce ani specializace — tu informaci tam nepotřebujeme.
    // Defenzivní pravidla pro dlouhé hodnoty:
    //   - jméno: truncate s "..." (priorita pro shrinking)
    //   - chips: flexShrink 0 (nemění se), max 2 specializace + "+X" indikátor
    //   - funkce chip: zobrazuje JEN reálně přiřazenou roli (greedy assignment), tooltip má plný seznam
    const renderItem = (o: Odpoved, jedu: boolean, large: boolean) => {
        const nestiha = jeNestihajici(o);
        const barvaPruhu = nestiha ? theme.palette.warning.main : (jedu ? '#2e7d32' : '#c62828');
        const specs = o.specializace ?? [];
        const specsVisible = specs.slice(0, MAX_SPEC_VISIBLE);
        const specsOverflow = Math.max(0, specs.length - specsVisible.length);
        const allFunkceLabel = o.funkce?.map(f => f.kod).join(', ') ?? '';
        const assignedRole = jedu ? assignedRoleById.get(o.id) : undefined;
        const funkceTooltip = allFunkceLabel
            ? (assignedRole && o.funkce && o.funkce.length > 1
                ? `${allFunkceLabel} — započítán jako ${assignedRole}`
                : allFunkceLabel)
            : '';
        return (
            <Box
                key={o.id}
                data-nestiha={nestiha ? 'true' : 'false'}
                sx={{
                    px: 1, py: large ? 0.6 : 0.3, mb: 0.5,
                    display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 0.5,
                    breakInside: 'avoid', borderRadius: 0.5,
                    borderLeft: '3px solid',
                    borderLeftColor: barvaPruhu,
                    backgroundColor: nestiha ? podkladNestiha : (jedu ? cardTintJedu : cardTintNejedu),
                    minWidth: 0, overflow: 'hidden',
                }}
            >
                <Typography
                    title={o.jmeno}
                    sx={{
                        fontSize: large ? '1.85rem' : '1.3rem',
                        fontWeight: 'bold', minWidth: 0, flex: '1 1 0',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                    }}
                >
                    {o.jmeno}
                </Typography>
                {/* Specializace (vlevo, hned za jménem) — jen pro JEDU */}
                {jedu && showSpecializace && specsVisible.map(s => (
                    <Chip
                        key={s.id}
                        label={s.kod}
                        title={s.kod}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{
                            height: large ? 28 : 24,
                            fontSize: '1rem',
                            flexShrink: 0,
                            maxWidth: large ? 90 : 70,
                            '& .MuiChip-label': { px: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' },
                        }}
                    />
                ))}
                {jedu && showSpecializace && specsOverflow > 0 && (
                    <Typography
                        title={specs.map(s => s.kod).join(', ')}
                        sx={{
                            fontSize: '1rem',
                            fontWeight: 700, color: 'text.secondary',
                            flexShrink: 0,
                        }}
                    >
                        +{specsOverflow}
                    </Typography>
                )}
                {/* Role a odpocet dojezdu NAD SEBOU na pravem okraji.
                    Vedle sebe zabiraly sirku dvakrat (role 25 px + odpocet 56 px + mezera),
                    a jmeno se pak pri dvou specializacich orezavalo uz kolem 143 px. Ve sloupci
                    zabere dvojice jen sirku toho sirsiho z nich a jmeno tim ziska ~34 px.

                    Role je zamerne PROSTY TEXT, ne Chip: chip ma vlastni vyplen a pozadi, ktere
                    ukusuji vysku navic, a odpocet by se pak musel zmensit ze 17,6 na 13 px. Takhle
                    se dvojice vejde do stavajici vysky radku a odpocet si drzi puvodni velikost
                    (zmereno v prohlizeci: radek zustava 36 px kompaktni / 54 px velky, protoze
                    vysku radku dal urcuje jmeno, ne tenhle sloupec).
                    Role i kompaktni odpocet maji 16 px (minimum kiosku); role s lineHeight 0,95
                    (15,2 px) + odpocet 16 px = 31,2 px, tedy presne vyska jmena 1,3rem x 1,5.
                    Ta vyska je podstatna: pocet zobrazenych radku se pocita z konstant nize
                    (itemHJedu / itemHNejedu), takze vyssi radek by tise rozhodil "+ X dalsich". */}
                {((jedu && showFunkce && assignedRole) || (o.dojezdCas && casVyhlaseni)) && (
                    <Box
                        sx={{
                            display: 'flex', flexDirection: 'column',
                            alignItems: 'flex-end', justifyContent: 'center',
                            flexShrink: 0, ml: 'auto', lineHeight: 1,
                        }}
                    >
                        {jedu && showFunkce && assignedRole && (
                            <Typography
                                title={funkceTooltip}
                                sx={{
                                    // min. 16 px (čitelnost ze 3 m)
                                    fontSize: '1rem',
                                    // kompaktne: 15,2 + 16 px = 31,2 px = presne vyska radku jmena (1,3rem x 1,5)
                                    fontWeight: 800, lineHeight: large ? 1 : 0.95,
                                    // primary.main je zelena a splyvala by s zelenym podkresem radku JEDU
                                    color: 'info.main',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {assignedRole}
                            </Typography>
                        )}
                        {o.dojezdCas && casVyhlaseni && (
                            <DojezdOdpocet
                                dojezdCas={o.dojezdCas}
                                casVyhlaseni={casVyhlaseni}
                                fontSize={large ? '1.5rem' : '1rem'}
                            />
                        )}
                    </Box>
                )}
            </Box>
        );
    };

    // Řádek na výšku: bez barevného pruhu na levé hraně, jen tónované pozadí (oranžové u nestíhajících).
    const renderRadekNaVysku = (o: Odpoved, jedu: boolean) => {
        const nestiha = jeNestihajici(o);
        const specs = o.specializace ?? [];
        const specsVisible = specs.slice(0, MAX_SPEC_VISIBLE);
        const specsOverflow = Math.max(0, specs.length - specsVisible.length);
        const assignedRole = jedu ? assignedRoleById.get(o.id) : undefined;
        return (
            <Box key={o.id} data-radek="true" data-nestiha={nestiha ? 'true' : 'false'} sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.75, py: 0.75, mb: 0.75, minHeight: 52,
                boxSizing: 'border-box', borderRadius: 0.75, minWidth: 0, overflow: 'hidden',
                backgroundColor: nestiha ? podkladNestiha : (jedu ? cardTintJedu : cardTintNejedu),
            }}>
                <Typography title={o.jmeno} sx={{
                    fontSize: jedu ? '1.875rem' : '1.625rem', fontWeight: jedu ? 700 : 600, lineHeight: 1.2,
                    minWidth: 0, flex: '1 1 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                    {o.jmeno}
                </Typography>
                {jedu && showSpecializace && specsVisible.map(s => (
                    <Chip key={s.id} label={s.kod} title={s.kod} size="small" variant="outlined" color="secondary" sx={{
                        height: 30, fontSize: '1.125rem', flexShrink: 0, maxWidth: 110,
                        '& .MuiChip-label': { px: 0.75, overflow: 'hidden', textOverflow: 'ellipsis' },
                    }} />
                ))}
                {jedu && showSpecializace && specsOverflow > 0 && (
                    <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: 'text.secondary', flexShrink: 0 }}>
                        +{specsOverflow}
                    </Typography>
                )}
                {jedu && showFunkce && assignedRole && (
                    <Typography data-testid="radek-role" sx={{
                        fontSize: '1.25rem', fontWeight: 800, color: 'info.main', flexShrink: 0, minWidth: 28, textAlign: 'right',
                    }}>
                        {assignedRole}
                    </Typography>
                )}
                {jedu && o.dojezdCas && casVyhlaseni && (
                    <Box sx={{ flexShrink: 0, minWidth: 76, textAlign: 'right' }}>
                        <DojezdOdpocet dojezdCas={o.dojezdCas} casVyhlaseni={casVyhlaseni} fontSize="1.625rem" />
                    </Box>
                )}
            </Box>
        );
    };

    if (rozlozeni === 'vyska') {
        const nJedu = viditelneRadky(mereniNaVysku.jedu.sloupec, mereniNaVysku.jedu.radek, jedouOdpovedi.length, rezervaDalsich(mereniNaVysku.jedu));
        const nNejedu = viditelneRadky(mereniNaVysku.nejedu.sloupec, mereniNaVysku.nejedu.radek, nejedouOdpovedi.length, rezervaDalsich(mereniNaVysku.nejedu));
        // position: relative kvůli skrytému měřicímu řádku „+ N dalších" (absolutně, mimo tok).
        const sloupecSx = { position: 'relative', minWidth: 0, px: 2, py: 1.5, overflow: 'hidden', boxSizing: 'border-box' } as const;
        const dalsiSx = { fontSize: '1.25rem', lineHeight: 1.5, color: 'text.secondary', pl: 1.75 } as const;
        // Skrytý řádek se stejným stylem jako „+ N dalších": změří se i tehdy, když se vejdou všechna jména.
        const mericiRadekDalsich = (
            <Typography aria-hidden="true" data-mereni-dalsi="true" sx={{
                ...dalsiSx, position: 'absolute', top: 0, left: 0, visibility: 'hidden', pointerEvents: 'none', whiteSpace: 'nowrap',
            }}>+ 0 dalších</Typography>
        );
        return (
            // color: text.primary – lokální (noční) theme z Vyjezd2026Wrapper nemá vlastní CssBaseline, takže zděděná
            // barva textu je z globálního světlého theme (tmavá). Na šířku to řeší Paper, na výšku je to obyčejný Box.
            <Box data-testid="odpovedi-na-vysku" sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden', bgcolor: 'background.paper', color: 'text.primary' }}>
                <Box data-testid="vyska-sekce-semafor" sx={{ height: `${vyskaSemaforuPx}px`, flex: 'none', display: 'flex', borderBottom: '2px solid', borderColor: 'divider' }}>
                    <VyjezdSemafor
                        orientation="row"
                        pocetJedu={jedouOdpovedi.length}
                        pocetNejedu={nejedouOdpovedi.length}
                        pocetVD={pocetVD}
                        pocetS={pocetS}
                        pocetH={pocetH}
                        pravidla={pravidla}
                    />
                </Box>
                <Box data-testid="vyska-sekce-jmena" sx={{ flex: 1, minHeight: 0, display: 'flex' }}>
                    <Box ref={sloupecJeduRef} data-testid="jmena-jedu" data-sloupec="jedu"
                        sx={{ ...sloupecSx, flexGrow: 1.7, flexShrink: 1, flexBasis: 0, borderRight: '2px solid', borderColor: 'divider' }}>
                        {mericiRadekDalsich}
                        {jedouOdpovedi.slice(0, nJedu).map(o => renderRadekNaVysku(o, true))}
                        {jedouOdpovedi.length > nJedu && (
                            <Typography data-testid="jmena-jedu-dalsi" sx={dalsiSx}>+ {jedouOdpovedi.length - nJedu} dalších</Typography>
                        )}
                        <ZalohaSkupina polozky={rozdeleni.zaloha} fontSize="1.3rem" />
                    </Box>
                    <Box ref={sloupecNejeduRef} data-testid="jmena-nejedu" data-sloupec="nejedu"
                        sx={{ ...sloupecSx, flexGrow: 1, flexShrink: 1, flexBasis: 0 }}>
                        {mericiRadekDalsich}
                        {nejedouOdpovedi.slice(0, nNejedu).map(o => renderRadekNaVysku(o, false))}
                        {nejedouOdpovedi.length > nNejedu && (
                            <Typography data-testid="jmena-nejedu-dalsi" sx={dalsiSx}>+ {nejedouOdpovedi.length - nNejedu} dalších</Typography>
                        )}
                    </Box>
                </Box>
            </Box>
        );
    }

    const jedouVisible = jedouOdpovedi.length > maxItemsJedu
        ? jedouOdpovedi.slice(0, maxItemsJedu - 1)
        : jedouOdpovedi;
    const jedouOverflow = Math.max(0, jedouOdpovedi.length - jedouVisible.length);

    const nejedouVisible = nejedouOdpovedi.length > maxItemsNejedu
        ? nejedouOdpovedi.slice(0, maxItemsNejedu - 1)
        : nejedouOdpovedi;
    const nejedouOverflow = Math.max(0, nejedouOdpovedi.length - nejedouVisible.length);

    return (
        <Paper
            elevation={0}
            square
            sx={{
                flex: 1, p: 0, display: 'flex', flexDirection: 'row', minHeight: 0, overflow: 'hidden',
                borderRadius: 0,
            }}
        >
            {/* Vlevo (přitisknutý ke kraji karty): vertikální semafor (JEDU → role → NEJEDU → plomba) */}
            <Box sx={{ flex: '0 0 auto', display: 'flex' }}>
                <VyjezdSemafor
                    orientation="vertical"
                    pocetJedu={jedouOdpovedi.length}
                    pocetNejedu={nejedouOdpovedi.length}
                    pocetVD={pocetVD}
                    pocetS={pocetS}
                    pocetH={pocetH}
                    pocetZaloha={pocetZaloha}
                    pravidla={pravidla}
                />
            </Box>
            {/* Vpravo: jména JEDU/NEJEDU ve dvou sloupcích (bez hlaviček — počty jsou v semaforu vlevo).
                pt: 0 — první karta sedí přímo na horní hraně sekce (žádný odstup nahoře). */}
            <Box ref={containerRef} sx={{ flex: 1, pt: 0, px: 1, pb: 1, display: 'flex', gap: 1, minHeight: 0, overflow: 'hidden' }}>
                {/* JEDU sloupec — širší, drží jména s funkcemi a specializacemi */}
                <Box sx={{ flex: 1.6, minWidth: 0 }}>
                    {jedouVisible.map(o => renderItem(o, true, useAdaptiveJedu))}
                    {jedouOverflow > 0 && (
                        <Typography sx={{ fontSize: '1rem', color: 'text.secondary', pl: 1 }}>
                            + {jedouOverflow} dalších
                        </Typography>
                    )}
                    <ZalohaSkupina polozky={rozdeleni.zaloha} fontSize="1.3rem" maxViditelnych={maxZaloha} />
                </Box>
                {/* NEJEDU sloupec — užší (bez chips), drží pouze jména */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {nejedouVisible.map(o => renderItem(o, false, useAdaptiveNejedu))}
                    {nejedouOverflow > 0 && (
                        <Typography sx={{ fontSize: '1rem', color: 'text.secondary', pl: 1 }}>
                            + {nejedouOverflow} dalších
                        </Typography>
                    )}
                </Box>
            </Box>
        </Paper>
    );
};
